import type { StoryProject, StoryState } from './types'
import { applyEffects, choiceAvailable, initialStoryState } from './storyLogic'

export type PlayFrame = {
  nodeId: string
  lineIndex: number
  state: StoryState
  choiceId?: string
}
export type Playthrough = PlayFrame & {
  originId: string
  history: PlayFrame[]
  errors: string[]
}

export function createPlaythrough(
  project: StoryProject,
  requestedId = project.startNodeId,
): Playthrough {
  const node =
    project.nodes.find((item) => item.id === requestedId) ?? project.nodes[0]
  const { state, errors } = applyEffects(
    initialStoryState(project),
    node.effects ?? [],
    project.variables ?? [],
  )
  return {
    nodeId: node.id,
    originId: node.id,
    lineIndex: 0,
    state,
    history: [],
    errors,
  }
}

export function takeRoute(
  project: StoryProject,
  play: Playthrough,
  choiceId?: string,
): Playthrough {
  const node = project.nodes.find((item) => item.id === play.nodeId)
  if (
    !node ||
    node.kind === 'ending' ||
    play.lineIndex < node.blocks.length ||
    play.errors.length
  )
    return play
  const choice = choiceId
    ? node.choices.find((item) => item.id === choiceId)
    : undefined
  if (node.choices.length && !choice) return play
  if (choice && !choiceAvailable(choice, play.state, project.variables ?? []))
    return play
  const target = project.nodes.find(
    (item) => item.id === (choice ? choice.targetId : node.nextId),
  )
  if (!target) return play
  // Consequences happen in order: the choice first, then entry into the destination.
  const result = applyEffects(
    play.state,
    [...(choice?.effects ?? []), ...(target.effects ?? [])],
    project.variables ?? [],
  )
  if (result.errors.length) return { ...play, errors: result.errors }
  return {
    ...play,
    nodeId: target.id,
    lineIndex: 0,
    state: result.state,
    errors: [],
    history: [
      ...play.history,
      {
        nodeId: play.nodeId,
        lineIndex: play.lineIndex,
        state: { ...play.state },
        ...(choice ? { choiceId: choice.id } : {}),
      },
    ],
  }
}

export function advancePlaythrough(
  project: StoryProject,
  play: Playthrough,
): Playthrough {
  const node = project.nodes.find((item) => item.id === play.nodeId)
  if (!node) return play
  return play.lineIndex < node.blocks.length
    ? { ...play, lineIndex: play.lineIndex + 1 }
    : takeRoute(project, play)
}

export function rewindPlaythrough(play: Playthrough): Playthrough {
  if (play.lineIndex > 0) return { ...play, lineIndex: play.lineIndex - 1 }
  const previous = play.history.at(-1)
  if (!previous) return play
  return {
    ...play,
    ...previous,
    state: { ...previous.state },
    errors: [],
    history: play.history.slice(0, -1),
  }
}
